import {
  Business,
  CreditCard,
  People,
  PersonAdd,
  Send,
  TrendingUp,
} from "@mui/icons-material";
import {
  Box,
  Chip,
  Grid,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { LineChart } from "@mui/x-charts/LineChart";
import { PieChart } from "@mui/x-charts/PieChart";
import axios from "axios";
import { User } from "../../utils";
import moment from "moment";
import { useEffect, useState } from "react";
import { hostname } from "../../utils";
import MetricCard from "../sequences/SequenceMetricsCard";

interface WhitelabelAnalytics {
  whitelabel: {
    id: string;
    domain?: string;
    title: string;
    created_date?: string;
  };
  metrics: {
    total_customers: number;
    total_companies: number;
    recent_signups: number;
    usage: {
      total_credits: number;
      total_tokens: number;
      total_requests: number;
      active_users: number;
      recent_credits: number;
      recent_requests: number;
      recent_active_users: number;
    };
  };
  charts: {
    daily_signups: Array<{
      date: string;
      signups: number;
    }>;
    feature_usage: Array<{
      feature: string;
      credits: number;
      requests: number;
    }>;
    model_usage: Array<{
      model: string;
      credits: number;
      requests: number;
    }>;
  };
}

export default function WhitelabelAnalytics({
  currentUser,
  getToken,
  whitelabelId,
}: {
  currentUser: User;
  getToken: Function;
  whitelabelId: string;
}) {
  const [loading, setLoading] = useState(false);
  const [analytics, setAnalytics] = useState<WhitelabelAnalytics | null>(null);

  const loadAnalytics = async () => {
    setLoading(true);
    const currentToken = await getToken();

    try {
      const response = await axios.get(`${hostname}/whitelabel/${whitelabelId}/analytics`, {
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      });

      setAnalytics(response.data);
    } catch (err) {
      console.error("Error loading analytics:", err);
      // Handle specific error cases
      if (axios.isAxiosError(err)) {
        if (err.response?.status === 403) {
          console.error("Access denied - user is not authorized to view analytics for this whitelabel instance");
        } else if (err.response?.status === 404) {
          console.error("Whitelabel instance not found");
        }
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser.uid && whitelabelId) {
      loadAnalytics();
    }
  }, [currentUser.uid, whitelabelId]);

  if (loading) {
    return <LinearProgress sx={{ mt: 2 }} />;
  }

  if (!analytics) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="text.secondary">
          No analytics data available
        </Typography>
      </Box>
    );
  }

  const signupChartData = analytics.charts.daily_signups.map(item => ({
    date: moment(item.date).format("MMM DD"),
    signups: item.signups,
  }));

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          Analytics Dashboard
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {analytics.whitelabel.title} ({analytics.whitelabel.domain || 'No domain'})
        </Typography>
      </Box>

      {/* Key Metrics */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={<People color="primary" />}
            title="Total Customers"
            value={analytics.metrics.total_customers.toLocaleString()}
            color="primary.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={<Business color="info" />}
            title="Total Companies"
            value={analytics.metrics.total_companies.toLocaleString()}
            color="info.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={<PersonAdd color="success" />}
            title="New Signups (30d)"
            value={analytics.metrics.recent_signups.toLocaleString()}
            color="success.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={<TrendingUp color="warning" />}
            title="Active Users"
            value={analytics.metrics.usage.active_users.toLocaleString()}
            color="warning.main"
          />
        </Grid>
      </Grid>

      {/* Usage Metrics */}
      <Paper sx={{ p: 3, mb: 3 }} variant="outlined">
        <Typography variant="h6" gutterBottom>
          Usage Statistics
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Box textAlign="center">
              <Typography variant="h4" color="primary.main" fontWeight="bold">
                {analytics.metrics.usage.total_credits.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Credits Used
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box textAlign="center">
              <Typography variant="h4" color="info.main" fontWeight="bold">
                {analytics.metrics.usage.total_requests.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Requests
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box textAlign="center">
              <Typography variant="h4" color="success.main" fontWeight="bold">
                {analytics.metrics.usage.recent_credits.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Recent Credits (30d)
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box textAlign="center">
              <Typography variant="h4" color="warning.main" fontWeight="bold">
                {analytics.metrics.usage.recent_active_users.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Recent Active Users (30d)
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Charts */}
      <Grid container spacing={3}>
        {/* Daily Signups Chart */}
        {analytics.charts.daily_signups.length > 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }} variant="outlined">
              <Typography variant="h6" gutterBottom>
                Daily Signups (Last 30 Days)
              </Typography>
              <Box sx={{ height: 300 }}>
                <LineChart
                  series={[
                    {
                      data: signupChartData.map(item => item.signups),
                      label: "New Signups",
                      color: "#1976d2",
                    },
                  ]}
                  xAxis={[
                    {
                      data: signupChartData.map(item => item.date),
                      scaleType: "point",
                    },
                  ]}
                  height={300}
                />
              </Box>
            </Paper>
          </Grid>
        )}

        {/* Feature Usage Pie Chart - Credits Distribution (Full Width Row) */}
        {analytics.charts.feature_usage.length > 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }} variant="outlined">
              <Typography variant="h6" gutterBottom>
                Credits by Feature
              </Typography>
              <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, alignItems: "center", gap: 3 }}>
                <Box sx={{ height: 300, width: { xs: "100%", md: 400 }, flexShrink: 0 }}>
                  {(() => {
                    const topFeatures = analytics.charts.feature_usage.slice(0, 6);
                    const totalCredits = topFeatures.reduce((sum, f) => sum + f.credits, 0);
                    return (
                      <PieChart
                        series={[
                          {
                            data: topFeatures.map((item, index) => ({
                              id: index,
                              value: item.credits,
                              label: item.feature.length > 20
                                ? item.feature.substring(0, 18) + "..."
                                : item.feature,
                            })),
                            arcLabel: (item) => {
                              const percent = totalCredits > 0
                                ? ((item.value / totalCredits) * 100).toFixed(0)
                                : "0";
                              return `${percent}%`;
                            },
                            arcLabelMinAngle: 20,
                            highlightScope: { faded: "global", highlighted: "item" },
                            faded: { innerRadius: 30, additionalRadius: -30, color: "gray" },
                          },
                        ]}
                        height={300}
                        slotProps={{
                          legend: {
                            hidden: true,
                          },
                        }}
                      />
                    );
                  })()}
                </Box>
                {/* Legend to the right on desktop, below on mobile */}
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, justifyContent: { xs: "center", md: "flex-start" } }}>
                  {analytics.charts.feature_usage.slice(0, 6).map((item, index) => {
                    const colors = ["#1976d2", "#dc004e", "#9c27b0", "#ff9800", "#4caf50", "#00bcd4"];
                    const totalCredits = analytics.charts.feature_usage.slice(0, 6).reduce((sum, f) => sum + f.credits, 0);
                    const percent = totalCredits > 0 ? ((item.credits / totalCredits) * 100).toFixed(1) : "0";
                    return (
                      <Chip
                        key={index}
                        size="small"
                        label={`${item.feature} (${percent}%)`}
                        sx={{
                          backgroundColor: colors[index % colors.length],
                          color: "white",
                          fontSize: "0.75rem",
                        }}
                      />
                    );
                  })}
                </Box>
              </Box>
            </Paper>
          </Grid>
        )}

        {/* Credits by Feature - Horizontal Bar Chart */}
        {analytics.charts.feature_usage.length > 0 && (
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }} variant="outlined">
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <CreditCard color="primary" />
                <Typography variant="h6">
                  Credits Used by Feature
                </Typography>
              </Box>
              <Box sx={{ height: Math.max(300, analytics.charts.feature_usage.length * 40) }}>
                <BarChart
                  layout="horizontal"
                  series={[
                    {
                      data: analytics.charts.feature_usage.map(item => item.credits),
                      label: "Credits",
                      color: "#1976d2",
                    },
                  ]}
                  yAxis={[
                    {
                      data: analytics.charts.feature_usage.map(item => item.feature),
                      scaleType: "band",
                    },
                  ]}
                  height={Math.max(300, analytics.charts.feature_usage.length * 40)}
                  margin={{ left: 150 }}
                  slotProps={{
                    legend: {
                      hidden: true,
                    },
                  }}
                />
              </Box>
            </Paper>
          </Grid>
        )}

        {/* Requests by Feature - Horizontal Bar Chart */}
        {analytics.charts.feature_usage.length > 0 && (
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }} variant="outlined">
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <Send color="secondary" />
                <Typography variant="h6">
                  Requests by Feature
                </Typography>
              </Box>
              <Box sx={{ height: Math.max(300, analytics.charts.feature_usage.length * 40) }}>
                <BarChart
                  layout="horizontal"
                  series={[
                    {
                      data: analytics.charts.feature_usage.map(item => item.requests),
                      label: "Requests",
                      color: "#9c27b0",
                    },
                  ]}
                  yAxis={[
                    {
                      data: analytics.charts.feature_usage.map(item => item.feature),
                      scaleType: "band",
                    },
                  ]}
                  height={Math.max(300, analytics.charts.feature_usage.length * 40)}
                  margin={{ left: 150 }}
                  slotProps={{
                    legend: {
                      hidden: true,
                    },
                  }}
                />
              </Box>
            </Paper>
          </Grid>
        )}

        {/* Detailed Feature Usage Table */}
        {analytics.charts.feature_usage.length > 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }} variant="outlined">
              <Typography variant="h6" gutterBottom>
                Feature Usage Details
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Feature</strong></TableCell>
                      <TableCell align="right"><strong>Credits Used</strong></TableCell>
                      <TableCell align="right"><strong>Requests</strong></TableCell>
                      <TableCell align="right"><strong>Avg Credits/Request</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {analytics.charts.feature_usage.map((item, index) => (
                      <TableRow key={index} hover>
                        <TableCell>
                          <Typography variant="body2">
                            {item.feature}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {item.credits.toLocaleString()}
                        </TableCell>
                        <TableCell align="right">
                          {item.requests.toLocaleString()}
                        </TableCell>
                        <TableCell align="right">
                          {item.requests > 0
                            ? (item.credits / item.requests).toFixed(2)
                            : "0"}
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Totals row */}
                    <TableRow sx={{ backgroundColor: "action.hover" }}>
                      <TableCell><strong>Total</strong></TableCell>
                      <TableCell align="right">
                        <strong>
                          {analytics.charts.feature_usage
                            .reduce((sum, f) => sum + f.credits, 0)
                            .toLocaleString()}
                        </strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>
                          {analytics.charts.feature_usage
                            .reduce((sum, f) => sum + f.requests, 0)
                            .toLocaleString()}
                        </strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>
                          {(() => {
                            const totalCredits = analytics.charts.feature_usage.reduce((sum, f) => sum + f.credits, 0);
                            const totalRequests = analytics.charts.feature_usage.reduce((sum, f) => sum + f.requests, 0);
                            return totalRequests > 0 ? (totalCredits / totalRequests).toFixed(2) : "0";
                          })()}
                        </strong>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        )}

        {/* Detailed Model Usage Table */}
        {analytics.charts.model_usage.length > 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }} variant="outlined">
              <Typography variant="h6" gutterBottom>
                Model Usage Details
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Model</strong></TableCell>
                      <TableCell align="right"><strong>Credits Used</strong></TableCell>
                      <TableCell align="right"><strong>Requests</strong></TableCell>
                      <TableCell align="right"><strong>Avg Credits/Request</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {analytics.charts.model_usage.map((item, index) => (
                      <TableRow key={index} hover>
                        <TableCell>
                          <Typography variant="body2">
                            {item.model}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {item.credits.toLocaleString()}
                        </TableCell>
                        <TableCell align="right">
                          {item.requests.toLocaleString()}
                        </TableCell>
                        <TableCell align="right">
                          {item.requests > 0
                            ? (item.credits / item.requests).toFixed(2)
                            : "0"}
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Totals row */}
                    <TableRow sx={{ backgroundColor: "action.hover" }}>
                      <TableCell><strong>Total</strong></TableCell>
                      <TableCell align="right">
                        <strong>
                          {analytics.charts.model_usage
                            .reduce((sum, m) => sum + m.credits, 0)
                            .toLocaleString()}
                        </strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>
                          {analytics.charts.model_usage
                            .reduce((sum, m) => sum + m.requests, 0)
                            .toLocaleString()}
                        </strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>
                          {(() => {
                            const totalCredits = analytics.charts.model_usage.reduce((sum, m) => sum + m.credits, 0);
                            const totalRequests = analytics.charts.model_usage.reduce((sum, m) => sum + m.requests, 0);
                            return totalRequests > 0 ? (totalCredits / totalRequests).toFixed(2) : "0";
                          })()}
                        </strong>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}